/**
 * NavigationDropdown - Clickable badge that opens a full navigation panel.
 *
 * Replaces the static "COMMAND CENTER" badge in both the main CommandCenter
 * header and the shared CommandCenterHeader used by sub-pages.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import { useAuth } from '../../../auth/AuthProviderUnified';
import {
  getNavigationDropdownSections,
  QUICK_ACTION_ROUTES,
} from '../../stripe-ui/sidebarConfig';
import { Pressable, overlayBackdropVariants, overlayPanelVariants } from '../../common/motionSystem';
import './NavigationDropdown.css';

const NavigationDropdown = ({ label = 'Command Center', quickActions = null }) => {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const wrapperRef = useRef(null);

  const sections = getNavigationDropdownSections(user?.role);

  const toggle = useCallback(() => setIsOpen((prev) => !prev), []);
  const close = useCallback(() => setIsOpen(false), []);

  // Close on Escape key
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen, close]);

  const handleNavigate = useCallback(
    (path) => {
      navigate(path);
      close();
    },
    [navigate, close],
  );

  const handleQuickAction = useCallback(
    (actionId) => {
      const path = QUICK_ACTION_ROUTES[actionId];
      if (path) {
        navigate(path);
        close();
      }
    },
    [navigate, close],
  );

  return (
    <div className="nav-dropdown-wrapper" ref={wrapperRef}>
      <Pressable
        as="button"
        glow
        className="nav-dropdown-trigger"
        onClick={toggle}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <span className="nav-dropdown-label">{label}</span>
        <ChevronDown
          size={14}
          className={`nav-dropdown-chevron ${isOpen ? 'open' : ''}`}
        />
      </Pressable>

      <AnimatePresence>
        {isOpen && (
          <>
            <motion.div
              className="nav-dropdown-backdrop"
              variants={overlayBackdropVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              onClick={close}
            />
            <motion.div
              className="nav-dropdown-panel"
              variants={overlayPanelVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
            >
              {/* Quick Actions Row */}
              {quickActions && quickActions.length > 0 && (
                <>
                  <div className="nav-dropdown-quick-actions">
                    <span className="nav-dropdown-section-title">Quick Actions</span>
                    <div className="nav-dropdown-quick-row">
                      {quickActions.map((action) => {
                        const Icon = action.icon;
                        return (
                          <Pressable
                            as="button"
                            key={action.id}
                            className="nav-dropdown-quick-btn"
                            onClick={() => handleQuickAction(action.id)}
                            title={action.label}
                          >
                            <Icon size={16} />
                            <span>{action.label}</span>
                          </Pressable>
                        );
                      })}
                    </div>
                  </div>
                  <div className="nav-dropdown-divider" />
                </>
              )}

              {/* Navigation Sections */}
              <div className="nav-dropdown-sections">
                {sections.map((section) => (
                  <div key={section.title} className="nav-dropdown-section">
                    <span className="nav-dropdown-section-title">
                      {section.title}
                    </span>
                    {section.items.map((item) => {
                      const Icon = item.icon;
                      const isActive = location.pathname === item.path;
                      return (
                        <Pressable
                          as="button"
                          key={item.path}
                          className={`nav-dropdown-item ${isActive ? 'active' : ''}`}
                          onClick={() => handleNavigate(item.path)}
                        >
                          <Icon size={16} />
                          <span>{item.label}</span>
                        </Pressable>
                      );
                    })}
                  </div>
                ))}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

NavigationDropdown.propTypes = {
  label: PropTypes.string,
  quickActions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      icon: PropTypes.elementType.isRequired,
      label: PropTypes.string.isRequired,
    }),
  ),
};

export default NavigationDropdown;
